// Satır hesapları — backend routers/warehouse.py _compute_line_totals ile birebir (Python round = half-even).
export const CURRENCY_SYMBOL = { TRY: "₺", USD: "$", EUR: "€" };
export const CURRENCIES = ["TRY", "USD", "EUR"];

// Python round(x, 2) ile birebir: double'ın kesin ondalık değeri üzerinden en yakına, tam eşitlikte çifte
export const round2 = (x) => {
  const v = Number(x || 0);
  const s = Math.abs(v).toFixed(60);
  const frac = s.slice(s.indexOf(".") + 1);
  if (/^\d\d50*$/.test(frac)) {
    const f = Math.floor(Math.abs(v) * 100);
    return Math.sign(v) * (f % 2 === 0 ? f : f + 1) / 100;
  }
  return Number(v.toFixed(2));
};

export const fmtNum = (n, d = 2) =>
  Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: d, maximumFractionDigits: d });
export const fmtMoney = (n, cur = "TRY") => `${CURRENCY_SYMBOL[cur] || "₺"}${fmtNum(n)}`;

export const parseTr = (s) => {
  let t = String(s ?? "").trim().replace(/\s/g, "").replace(/[₺$€%]/g, "");
  if (!t) return 0;
  if (t.includes(",")) t = t.replace(/\./g, "").replace(",", ".");
  const v = Number(t);
  return Number.isFinite(v) ? v : 0;
};

export function computeLine(l) {
  const qty = Number(l.quantity || 0);
  const gross = qty * Number(l.unit_price || 0);
  const pct = gross * (Number(l.discount_percent || 0) / 100);
  const flat = Number(l.discount_amount || 0);
  const sub = Math.max(0, gross - pct - flat);
  const tax = sub * (Number(l.tax_rate || 0) / 100);
  let aSub = 0, aTax = 0;
  for (const a of l.applications || []) {
    const s = round2(Number(a.quantity || 0) * Number(a.unit_price || 0));
    aSub += s;
    aTax += round2(s * Number(a.tax_rate || 0) / 100);
  }
  const line_subtotal = round2(sub + aSub);
  const line_tax_total = round2(tax + aTax);
  return { gross, discount: pct + flat, line_subtotal, line_tax_total, line_total: round2(line_subtotal + line_tax_total) };
}

export function sumByCurrency(rows) {
  const acc = {};
  for (const r of rows) {
    const c = r.currency || "TRY";
    const t = computeLine(r);
    acc[c] ||= { gross: 0, discount: 0, subtotal: 0, tax: 0, total: 0, qty: 0 };
    acc[c].gross += t.gross;
    acc[c].discount += t.discount;
    acc[c].subtotal += t.line_subtotal;
    acc[c].tax += t.line_tax_total;
    acc[c].total += t.line_total;
    acc[c].qty += Number(r.quantity || 0);
  }
  return acc;
}

// Sipariş kur snapshot'ı ({EUR:{rate,date,source}}) veya düz TCMB ({EUR: 55.7}) kabul eder
export const rateOf = (fx, cur) => {
  if (cur === "TRY") return 1;
  const v = fx?.[cur];
  return Number((v && typeof v === "object" ? v.rate : v) || 0);
};

export function convertLine(l, to, fx) {
  const from = l.currency || "TRY";
  const k = rateOf(fx, from) / rateOf(fx, to);
  const cv = (x) => round2(Number(x || 0) * k);
  return {
    unit_price: cv(l.unit_price),
    discount_amount: cv(l.discount_amount),
    currency: to,
    applications: (l.applications || []).map((a) => ({ ...a, unit_price: cv(a.unit_price), currency: to })),
  };
}

export const EDIT_KEYS = ["quantity", "unit_price", "currency", "tax_rate", "discount_percent", "discount_amount"];
export const DETAIL_KEYS = ["product_id", "variant_id", "warehouse_id", "notes", "remaining_supply_method"];

// Tedarik yöntemi (satır): Stoktan Satış = stock_qty; kalan remaining_supply_method ile karşılanır
export const SUPPLY_OPTS = [["internal", "İç Üretim"], ["stock", "Stoktan Satış"], ["stock_mix", "Stok + Kalan İç Üretim"],
  ["ready_purchase", "Hazır Alım"], ["outsourced", "Fason Üretim"]];
export const supplyOf = (l) => {
  const sq = Number(l.stock_qty || 0), q = Number(l.quantity || 0);
  if (sq > 0 && sq >= q) return "stock";
  if (sq > 0) return "stock_mix";
  return l.stock_qty == null ? "" : (l.remaining_supply_method || "");
};
export const supplyPatch = (l, m, avail) => {
  const q = Number(l.quantity || 0);
  if (m === "stock") return { stock_qty: q, remaining_supply_method: "" };
  if (m === "stock_mix") { const s = Math.max(0, Math.min(q, Math.floor(Number(avail || 0)))); return { stock_qty: s, remaining_supply_method: "internal" }; }
  return { stock_qty: 0, remaining_supply_method: m };
};

// Kalemin tedarik yöntemi (backend _line_method ile aynı): satır kararı yoksa siparişin seçimi
export const SUPPLY_LABEL = { stock: "Stoktan Satış", internal: "İç Üretim", outsourced: "Fason Üretim", ready_purchase: "Hazır Alım", mixed: "Karma (Kalem Bazlı)" };
export const lineMethod = (order, l) => {
  if (l.stock_qty != null) return Number(l.remaining_qty || 0) <= 0 ? "stock" : (l.remaining_supply_method || "");
  const pm = order?.production_method;
  return ["internal", "outsourced", "ready_purchase"].includes(pm) ? pm : "internal";
};
export const supplySummary = (order) => {
  const out = {};
  for (const l of order?.line_items || []) {
    if (l.stock_qty == null) continue;
    if (Number(l.stock_qty) > 0) out.stock = (out.stock || 0) + Number(l.stock_qty);
    const m = lineMethod(order, l);
    if (m !== "stock" && m) out[m] = (out[m] || 0) + Number(l.remaining_qty || 0);
  }
  return out;
};

export function changedKeys(orig, row) {
  if (!orig) return ["new"];
  const out = [];
  for (const k of EDIT_KEYS) {
    const a = k === "currency" ? (orig.currency || "TRY") : Number(orig[k] || 0);
    const b = k === "currency" ? (row.currency || "TRY") : Number(row[k] || 0);
    if (a !== b) out.push(k);
  }
  for (const k of DETAIL_KEYS) if ((orig[k] || "") !== (row[k] || "")) out.push(k);
  if (Number(orig.stock_qty ?? -1) !== Number(row.stock_qty ?? -1)) out.push("stock_qty");
  if (row._appsChanged) out.push("applications");
  return out;
}
